const overlay = document.querySelector('[data-search-overlay]');

if (overlay) {
    const openButtons = document.querySelectorAll('[aria-controls="site-search"]');
    const closeButton = overlay.querySelector('[data-search-close]');
    const form = overlay.querySelector('[data-search-form]');
    const input = overlay.querySelector('[data-search-input]');
    const clearButton = overlay.querySelector('[data-search-clear]');
    const results = overlay.querySelector('[data-search-results]');
    const scrollArea = overlay.querySelector('.search-box__scrollArea');
    const status = overlay.querySelector('[data-search-status]');
    const loadingTemplate = overlay.querySelector('[data-search-loading-template]');
    const promptTemplate = overlay.querySelector('[data-search-prompt-template]');
    const errorTemplate = overlay.querySelector('[data-search-error-template]');
    const endpoint = overlay.dataset.searchEndpoint;
    const action = overlay.dataset.searchAction;
    const nonce = overlay.dataset.searchNonce;
    const minLength = Number.parseInt(overlay.dataset.searchMinLength || '2', 10);
    const debounceDelay = 260;

    let debounceTimeout = null;
    let activeRequest = null;
    let activeRequestId = 0;
    let previouslyFocused = null;
    let popularResultsHtml = '';

    const renderTemplate = (template) => {
        if (results && template) {
            results.innerHTML = template.innerHTML;
        }
    };

    const updateStatus = (message = '') => {
        if (status) {
            status.textContent = message;
        }
    };

    const setLoading = (isLoading) => {
        overlay.classList.toggle('is-loading', isLoading);
        results?.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    };

    const syncClearButton = () => {
        if (clearButton && input) {
            clearButton.hidden = input.value.length === 0;
        }
    };

    const cancelPendingRequest = () => {
        if (activeRequest) {
            activeRequest.abort();
            activeRequest = null;
        }
    };

    const showResults = (html, announcement = '') => {
        if (results) {
            results.innerHTML = html;
        }
        if (scrollArea) {
            scrollArea.scrollTop = 0;
        }
        updateStatus(announcement);
    };

    const requestResults = async (query = '', mode = 'search') => {
        cancelPendingRequest();

        if (mode === 'popular' && popularResultsHtml) {
            showResults(popularResultsHtml, 'Załadowano popularne produkty i kategorie.');
            setLoading(false);
            return;
        }

        const requestId = ++activeRequestId;
        activeRequest = new AbortController();
        setLoading(true);
        renderTemplate(loadingTemplate);
        updateStatus('Ładowanie wyników.');

        const body = new URLSearchParams({
            action,
            nonce,
            mode,
            s: query,
        });

        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
                },
                body: body.toString(),
                signal: activeRequest.signal,
            });
            const payload = await response.json();

            if (requestId !== activeRequestId) {
                return;
            }

            if (!response.ok || !payload.success || !payload.data?.html) {
                throw new Error(payload.data?.message || 'Search request failed.');
            }

            if (mode === 'popular') {
                popularResultsHtml = payload.data.html;
            }

            showResults(payload.data.html, payload.data.announcement || '');
        } catch (error) {
            if (error.name === 'AbortError') {
                return;
            }

            renderTemplate(errorTemplate);
            updateStatus('Nie udało się pobrać wyników.');
        } finally {
            if (requestId === activeRequestId) {
                activeRequest = null;
                setLoading(false);
            }
        }
    };

    const handleInput = () => {
        const query = input.value.trim();

        window.clearTimeout(debounceTimeout);
        cancelPendingRequest();
        syncClearButton();

        if (!query) {
            requestResults('', 'popular');
            return;
        }

        if (query.length < minLength) {
            activeRequestId += 1;
            setLoading(false);
            renderTemplate(promptTemplate);
            updateStatus(`Wpisz co najmniej ${minLength} znaki.`);
            return;
        }

        setLoading(true);
        renderTemplate(loadingTemplate);
        updateStatus('Ładowanie wyników.');
        debounceTimeout = window.setTimeout(() => requestResults(query, 'search'), debounceDelay);
    };

    const openSearch = (trigger = null) => {
        previouslyFocused = trigger || document.activeElement;
        overlay.classList.add('open');
        overlay.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('search-open');
        document.body.classList.add('search-open');
        openButtons.forEach((button) => button.setAttribute('aria-expanded', 'true'));

        window.requestAnimationFrame(() => {
            input?.focus();
            syncClearButton();

            const query = input?.value.trim() || '';
            if (query.length >= minLength) {
                requestResults(query, 'search');
            } else if (!query) {
                requestResults('', 'popular');
            } else {
                renderTemplate(promptTemplate);
            }
        });
    };

    const closeSearch = () => {
        window.clearTimeout(debounceTimeout);
        cancelPendingRequest();
        activeRequestId += 1;
        setLoading(false);
        overlay.classList.remove('open');
        overlay.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('search-open');
        document.body.classList.remove('search-open');
        openButtons.forEach((button) => button.setAttribute('aria-expanded', 'false'));

        if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
            previouslyFocused.focus();
        }
    };

    const trapFocus = (event) => {
        if (event.key !== 'Tab' || !overlay.classList.contains('open')) {
            return;
        }

        const focusable = Array.from(
            overlay.querySelectorAll(
                'a[href], button:not([disabled]):not([hidden]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
            ),
        ).filter((element) => element.offsetParent !== null);

        if (!focusable.length) {
            return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };

    openButtons.forEach((button) => {
        button.addEventListener('click', () => openSearch(button));
    });
    closeButton?.addEventListener('click', closeSearch);
    input?.addEventListener('input', handleInput);

    clearButton?.addEventListener('click', () => {
        input.value = '';
        syncClearButton();
        input.focus();
        requestResults('', 'popular');
    });

    form?.addEventListener('submit', (event) => {
        const query = input.value.trim();

        if (query.length >= minLength) {
            return;
        }

        event.preventDefault();
        input.focus();
        renderTemplate(promptTemplate);
        updateStatus(`Wpisz co najmniej ${minLength} znaki.`);
    });

    overlay.addEventListener('keydown', trapFocus);
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && overlay.classList.contains('open')) {
            closeSearch();
        }
    });

    window.requestAnimationFrame(() => overlay.classList.add('ready'));
}
